import React, { useState } from 'react';
import CustomModal from '../reusable/CustomModal';
import CustomButton from '../reusable/CustomButton';
import { executeWorkflowTransition, WorkflowTransitionResponse } from '../../services/workflowService';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import './ReportIssueModal.css';

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (transitionResponse: WorkflowTransitionResponse) => void;
}

const ReportIssueModal = ({ isOpen, onClose, onSuccess }: ReportIssueModalProps) => {
  const [issueDetails, setIssueDetails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen } = useErrorModal();

  const handleSend = async () => {
    if (!userDetails || !userDetails.eoaId || !userDetails.access_token || !issueDetails.trim()) return;

    try {
      setIsLoading(true);

      const metadata = {
        district_data_qc_rejected_at: new Date().toISOString(),
        district_data_qc_rejected_reason: issueDetails.trim(),
        district_data_qc_rejected_by: userDetails.name || 'Unknown',
        district_data_qc_rejected_by_role: userDetails.role || 'Unknown',
      };

      const transitionResponse = await executeWorkflowTransition(
        userDetails.eoaId,
        userDetails.access_token,
        'district_data_qc_rejection',
        metadata
      );

      onSuccess(transitionResponse);
      onClose();
      setIssueDetails(''); // Reset form
    } catch (error) {
      handleOpen('Error occurred while reporting the issue', error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIssueDetails('');
    onClose();
  };

  const isSendDisabled = !issueDetails.trim() || isLoading;

  return (
    <CustomModal isOpen={isOpen} handleClose={handleCancel} title="Report an issue" icon={false} maxWidth="sm">
      <p className="report-issue-description">
        If there's a problem with this data quality check, let us know and we'll reach out to follow up
      </p>

      <div className="report-issue-form">
        <label className="report-issue-label">
          <strong>Details of the issue</strong>
        </label>
        <textarea
          className="report-issue-textarea"
          value={issueDetails}
          onChange={(e) => setIssueDetails(e.target.value)}
          placeholder="Type here"
          rows={5}
          disabled={isLoading}
        />
      </div>

      <div className="report-issue-buttons">
        <CustomButton variant="secondary" buttonText="Cancel" buttonAction={handleCancel} disabled={isLoading} />
        <CustomButton
          variant="primary"
          buttonText="Send"
          buttonAction={handleSend}
          loading={isLoading}
          disabled={isSendDisabled}
        />
      </div>
    </CustomModal>
  );
};

export default ReportIssueModal;
